import { useQuery } from '@tanstack/react-query';
import api from '../../services/api';
import { parsePatientId } from './usePatientBasicInfo';
import { usePharmacyStore } from '../../stores/pharmacyStore';

export interface PatientNotesData {
  notes: any[];
  alerts: any[];
}

/**
 * PRX-80 Task 9 fix round 1 (item 9) — the query-key PREFIX this hook's own
 * query key is built from, exported so another mutation site (the Patient
 * 360 permanent-note card) can invalidate exactly what this tab reads without
 * a second, hand-copied array — one source of truth. TanStack Query's
 * `invalidateQueries` matches by prefix, so a caller with only the
 * `compositeId` (no `includeDeleted`/`limit`) still invalidates every variant
 * of this tab's own query.
 */
export const patientNotesQueryKeyPrefix = (compositeId: string | null | undefined) =>
  ['patient', 'notes', compositeId] as const;

/**
 * Hook to fetch patient notes
 * Only fetches when Notes tab is active
 */
export function usePatientNotes(
  patientId: string | undefined,
  options?: { enabled?: boolean; includeDeleted?: boolean; limit?: number }
) {
  const activePharmacy = usePharmacyStore((state) => state.activePharmacy);
  const { compositeId } = parsePatientId(patientId, activePharmacy?.id);

  const enabled = options?.enabled !== false && !!compositeId;
  const includeDeleted = options?.includeDeleted || false;
  const limit = options?.limit || 100;

  return useQuery({
    queryKey: [...patientNotesQueryKeyPrefix(compositeId), includeDeleted, limit],
    queryFn: async (): Promise<PatientNotesData> => {
      if (!compositeId) {
        throw new Error('Invalid patient ID');
      }

      const response = await api.get(`/api/v2/patients/${compositeId}/notes`, {
        params: {
          includeDeleted,
          limit
        }
      });

      const notesData = response.data?.notes || [];

      return {
        notes: notesData,
        alerts: notesData.filter((n: any) => n.isAlert || n.priority === 'critical')
      };
    },
    enabled,
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
}
